import { CdkDrag } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { PdfDocumentViewportsContainerService } from 'core/pdf-new';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, map, skip, take, takeUntil, tap } from 'rxjs';
import { ITask } from '../../../dto-map/i-task';
import { RemoteSplugParametersConfigurationService } from '../../../remotes/remote-splug-parameters-configuration.service';
import { SelectTasksStepService } from '../select-tasks-step.service';
import { TaskCollectionService } from '../task-collection.service';

@Injectable()
export class SignatureService {

  readonly TOOLBAR_HEIGHT = 35;

  readonly isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
  private readonly _destroy = new Subject<void>();

  readonly signatureTaskSubject = new BehaviorSubject<ITask<'SIGNATURE'>>(undefined);
  get signatureTask() {
    return this.signatureTaskSubject.value;
  }
  readonly uuidSubject = new BehaviorSubject<string>(undefined)
  get id() {
    return this.uuidSubject.value;
  }

  readonly tasksSubject = this._taskCollectionService.tasksSubject;
  readonly dragRefSubject = new BehaviorSubject<CdkDrag>(undefined);

  readonly PRE_UPDATE_PERIOD = 2.5 * 1000;
  readonly hangingSignatureTaskSubject = new BehaviorSubject<ITask<'SIGNATURE'>>(undefined)
  get #hangingSignatureTask() {
    return this.hangingSignatureTaskSubject.value
  }


  readonly viewportHeightSubject = this._pdfDocumentViewportsContainerService.viewportHeightSubject;
  readonly pdfDocumentViewportHeights = this._pdfDocumentViewportsContainerService.pdfDocumentViewportHeights;

  readonly pageInfosSubject = this._pdfDocumentViewportsContainerService.pdfPageInfosSubject
  readonly pagesLoadedObs = this.pageInfosSubject.pipe(map(Boolean));

  readonly pageHeightSubject = subjectFromObs({
    observable: combineLatest([
      this.hangingSignatureTaskSubject,
      this.pdfDocumentViewportHeights,
      this.pagesLoadedObs
    ])
      .pipe(
        filterAllTruthyInTuple(),
        map(([{ documentId }, pdfDocumentViewportHeighs]) => pdfDocumentViewportHeighs[documentId]),
      ),
    until: this._destroy,
    initialValue: 0
  })

  readonly scrollableWidthSubject = this._pdfDocumentViewportsContainerService.scrollableWidthSubject

  readonly positionSubject = subjectFromObs({
    observable: combineLatest([this.hangingSignatureTaskSubject, this.scrollableWidthSubject, this.pagesLoadedObs])
      .pipe(
        filterAllTruthyInTuple(),
        map(([{ documentId, configuration: { signaturePosition } }, scrollableWidth]) => {

          const pageWidth = this._pdfDocumentViewportsContainerService.getPageWidth(signaturePosition.pageNumber + 1, documentId)
          return {
            height: Math.round(signaturePosition.height),
            width: Math.round(signaturePosition.width),
            top: this._pdfDocumentViewportsContainerService.getScrollTopFromDocAndPage(signaturePosition.pageNumber + 1, documentId)
              + Math.round(signaturePosition.top)
              - this.TOOLBAR_HEIGHT,
            left: Math.round((scrollableWidth - pageWidth) / 2 + signaturePosition.left),
            'max-width': pageWidth
          }
        }),
      ),
    initialValue: null,
    until: this._destroy
  })

  readonly dragPositionObs = this.positionSubject.pipe(
    filter(Boolean),
    map(dimension => ({ x: dimension.left, y: dimension.top }))
  )

  get position() {
    return this.positionSubject.value;
  }

  readonly activitiesSubject = this._setSignaturesStepService.activitiesSubject;
  readonly actorSplugParamsSubject = this._remoteSplugParametersConfigurationService.localCollectionStore.stateSubject;

  readonly activityObs = combineLatest([
    this.activitiesSubject,
    this.hangingSignatureTaskSubject
  ])
    .pipe(
      filterAllTruthyInTuple(),
      map(([activities, signatureTask]) => activities.find(activity => activity.id === signatureTask.activityId)),
      distinctUntilChanged()
    )

  readonly hueObs = this.activityObs.pipe(
    filter(Boolean),
    map(({ actor }) => actor?.hue)
  );

  readonly backgroundObs = this.hueObs.pipe(
    map((hue) => 'hsla(' + (hue || 0) + ',100%,50%,0.2)')
  )

  readonly borderObs = this.hueObs.pipe(
    map((hue) => '.2rem solid hsla(' + (hue || 0) + ',100%,50%,0.6)')
  )


  readonly containerStyleObs = this.positionSubject.pipe(
    filter(Boolean),
    map((position) => ({
      height: position.height + this.TOOLBAR_HEIGHT + 'px',
      width: position.width + 'px',
      top: '0px',
      left: '0px',
      'max-width': position['max-width'],
      'resize': !this.isFirefox ? 'both' : null,
      'overflow': !this.isFirefox ? 'auto' : null
    }))
  )

  readonly signatureStyleObs = combineLatest([
    this.positionSubject,
    this.backgroundObs,
    this.borderObs
  ]).pipe(
    filterAllTruthyInTuple(),
    map(([position, background, border]) => ({
      height: 'calc(100% - ' + this.TOOLBAR_HEIGHT + 'px)',
      width: '100%',
      background,
      border
    }))
  )



  readonly setUpSignatureTypesObs = combineLatest([
    this.activityObs,
    this.actorSplugParamsSubject
  ])
    .pipe(
      filterAllTruthyInTuple(),
      map(([activity, allSignerSplugParams]) =>
        allSignerSplugParams
          .filter(({ actorId }) => actorId === activity?.actor?.id)
          .map((splugParams) => splugParams.sigTypeId)
      )
    )



  constructor(
    private readonly _taskCollectionService: TaskCollectionService,
    private readonly _setSignaturesStepService: SelectTasksStepService,
    private readonly _pdfDocumentViewportsContainerService: PdfDocumentViewportsContainerService,
    private readonly _remoteSplugParametersConfigurationService: RemoteSplugParametersConfigurationService

  ) {


    this.signatureTaskSubject.pipe(
      filter(Boolean)
    )
      .subscribe((signatureTask) => {

        if (!this.#hangingSignatureTask) {
          this.hangingSignatureTaskSubject.next({ ...signatureTask })
          return;
        }

        this.hangingSignatureTaskSubject.next({
          ...this.signatureTask,
          configuration: {
            signaturePosition: this.#hangingSignatureTask.configuration.signaturePosition,
            allowedSignatureTypes: signatureTask.configuration.allowedSignatureTypes,
            batchName: signatureTask.configuration.batchName,
            pades: signatureTask.configuration.pades,
            signatureVisible: signatureTask.configuration.signatureVisible
          },
          documentId: this.#hangingSignatureTask.documentId,
        })

      })


    this.hangingSignatureTaskSubject.pipe(
      takeUntil(this._destroy),
      filter(Boolean),
      skip(1),
      distinctUntilChanged((prev, next) => {

        const prevPosition = prev.configuration.signaturePosition
        const nextPosition = next.configuration.signaturePosition;

        return JSON.stringify(prevPosition) === JSON.stringify(nextPosition);

      }),
      debounceTime(this.PRE_UPDATE_PERIOD)
    )
      .subscribe((hangingSignatureTask) => {

        this.save(hangingSignatureTask);
      });

  }


  save(signature: ITask<'SIGNATURE'>) {

    this._taskCollectionService.updateTask(this.signatureTask.id, signature)
  }

  remove() {


    this._taskCollectionService.removeTask(this.signatureTask.id || '')
      .pipe(
        take(1),
        tap(() => {
          this.hangingSignatureTaskSubject.next(null);
        }),
      )
      .subscribe()
  }

  ngOnDestroy() {

    const hangingSignatureTask = this.hangingSignatureTaskSubject.value;
    if (JSON.stringify(hangingSignatureTask.configuration.signaturePosition) !== JSON.stringify(this.signatureTask.configuration.signaturePosition) && hangingSignatureTask) {
      this.save(hangingSignatureTask);
    }

    this._destroy.next()
    this._destroy.complete()
  }
}



